import homeContentDefaults from "../content/homeContentDefaults";
import {
    AnnouncementContent,
    HeroContent,
    SiteContentValues,
    StatContent
} from "../models/SiteContent";
import {
    CONTENT_DRAFT_MAX_SKEW_MS,
    CONTENT_DRAFT_SCHEMA_VERSION,
    CONTENT_DRAFT_TTL_MS,
    createContentDraftStore
} from "./contentDraftStore";
import useRevisionedContent, {RevisionedContentState} from "./useRevisionedContent";

export const SITE_CONTENT_URL = '/site-content';
export const SITE_CONTENT_TYPE = 'home';
export const SITE_CONTENT_DRAFT_PREFIX = 'siteContentDraft:';
// Re-exported under the historical names - the envelope machinery itself now
// lives in contentDraftStore and is shared with the other content documents.
export const SITE_CONTENT_DRAFT_SCHEMA_VERSION = CONTENT_DRAFT_SCHEMA_VERSION;
export const SITE_CONTENT_DRAFT_TTL_MS = CONTENT_DRAFT_TTL_MS;
export const SITE_CONTENT_DRAFT_MAX_SKEW_MS = CONTENT_DRAFT_MAX_SKEW_MS;

// Single source of truth for the field limits - the backend's pydantic models,
// the admin's yup schema and parseSiteContentValues all agree on these.
export const SITE_CONTENT_LIMITS = {
    title: 120,
    subtitle: 250,
    ctaPrimaryLabel: 30,
    ctaSecondaryLabel: 30,
    statValue: 15,
    statLabel: 40,
    announcementText: 200,
};

const STATS_COUNT = 3;

// GET /site-content answers with `Cache-Control: public, max-age=60`, which is
// right for the public hero and fatal for the admin: a read-after-write there
// is served from the browser cache with a stale `revision`, so every save after
// a 409 keeps sending the same stale expectedRevision and 409s forever.
// The admin therefore reads through a unique URL that no cache can match.
export const siteContentFreshUrl = () => `${SITE_CONTENT_URL}?_=${Date.now()}`;

const isFilledString = (value: any): value is string =>
    typeof value === 'string' && value.trim().length > 0;

// Shallow per-section validation: a malformed section is dropped whole and the
// default for that section is kept, so a bad payload can never blank the hero.
const validHero = (hero: any): HeroContent | null =>
    hero && typeof hero === 'object'
    && isFilledString(hero.title)
    && isFilledString(hero.subtitle)
    && isFilledString(hero.ctaPrimaryLabel)
    && isFilledString(hero.ctaSecondaryLabel)
        ? {
            title: hero.title,
            subtitle: hero.subtitle,
            ctaPrimaryLabel: hero.ctaPrimaryLabel,
            ctaSecondaryLabel: hero.ctaSecondaryLabel
        }
        : null;

const validStats = (stats: any): StatContent[] | null =>
    Array.isArray(stats)
    && stats.length === STATS_COUNT
    && stats.every((stat: any) => stat && typeof stat === 'object'
        && isFilledString(stat.value) && isFilledString(stat.label))
        ? stats.map((stat: any) => ({value: stat.value, label: stat.label}))
        : null;

// `enabled` is what actually puts the bar on the site. Documents stored before
// the field existed have no `enabled` at all - those read as false, so nothing
// ever self-publishes.
const validAnnouncement = (announcement: any): AnnouncementContent | null =>
    announcement && typeof announcement === 'object' && isFilledString(announcement.text)
        ? {text: announcement.text, enabled: announcement.enabled === true}
        : null;

// Merges section-whole over the defaults - never field by field.
export const mergeSiteContent = (incoming: any): SiteContentValues => ({
    hero: validHero(incoming?.hero) ?? homeContentDefaults.hero,
    stats: validStats(incoming?.stats) ?? homeContentDefaults.stats,
    announcement: validAnnouncement(incoming?.announcement) ?? homeContentDefaults.announcement,
});

// Returns the TRIMMED value - the backend strips whitespace before enforcing its
// limits, and returning the raw value would let whitespace-padded strings of any
// size through the "bounded" check.
const boundedString = (value: any, limit: number): string | null => {
    if (!isFilledString(value)) return null;
    const trimmed = value.trim();
    return trimmed.length <= limit ? trimmed : null;
};

// STRICT, all-or-nothing validation of an untrusted values object: every field
// must be a non-empty string within its limit and there must be exactly 3 stats.
// Used for anything that never went through the backend's validators - i.e. the
// localStorage preview drafts, and the admin's own values before it opens one.
export const parseSiteContentValues = (input: unknown): SiteContentValues | null => {
    const raw = input as any;
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const {hero, stats, announcement} = raw;
    if (!hero || typeof hero !== 'object' || !announcement || typeof announcement !== 'object') {
        return null;
    }

    const title = boundedString(hero.title, SITE_CONTENT_LIMITS.title);
    const subtitle = boundedString(hero.subtitle, SITE_CONTENT_LIMITS.subtitle);
    const ctaPrimaryLabel = boundedString(hero.ctaPrimaryLabel, SITE_CONTENT_LIMITS.ctaPrimaryLabel);
    const ctaSecondaryLabel = boundedString(hero.ctaSecondaryLabel, SITE_CONTENT_LIMITS.ctaSecondaryLabel);
    const text = boundedString(announcement.text, SITE_CONTENT_LIMITS.announcementText);

    if (!title || !subtitle || !ctaPrimaryLabel || !ctaSecondaryLabel || !text) {
        return null;
    }
    if (!Array.isArray(stats) || stats.length !== STATS_COUNT) {
        return null;
    }

    const parsedStats: StatContent[] = [];
    for (const stat of stats) {
        if (!stat || typeof stat !== 'object') {
            return null;
        }
        const value = boundedString(stat.value, SITE_CONTENT_LIMITS.statValue);
        const label = boundedString(stat.label, SITE_CONTENT_LIMITS.statLabel);
        if (!value || !label) {
            return null;
        }
        parsedStats.push({value, label});
    }

    return {
        hero: {title, subtitle, ctaPrimaryLabel, ctaSecondaryLabel},
        stats: parsedStats,
        announcement: {text, enabled: announcement.enabled === true},
    };
};

export const siteContentDraftStore = createContentDraftStore<SiteContentValues>(
    SITE_CONTENT_TYPE,
    SITE_CONTENT_DRAFT_PREFIX,
    parseSiteContentValues,
);

// Thin aliases kept for the existing call sites/tests.
export const readSiteContentDraft = siteContentDraftStore.read;
export const writeSiteContentDraft = siteContentDraftStore.write;
export const purgeExpiredSiteContentDrafts = siteContentDraftStore.purgeExpired;

export type SiteContentState = RevisionedContentState<SiteContentValues>;

export interface UseSiteContentOptions {
    // False when a SiteContentProvider above already owns the fetch.
    enabled?: boolean;
}

const useSiteContent = (options: UseSiteContentOptions = {}): SiteContentState =>
    useRevisionedContent<SiteContentValues>({
        url: SITE_CONTENT_URL,
        defaults: homeContentDefaults,
        mergeContent: mergeSiteContent,
        draftStore: siteContentDraftStore,
        enabled: options.enabled ?? true,
    });

export default useSiteContent;
